import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import jsPDF from 'jspdf';
import { db, storage } from '../../../firebase';
import { PortvilleRoom, PortvilleResidency, PortvilleRoomWithResidency } from '../../../types/portvilleRoom';
import usePortvilleRoomAuth from '../../../hooks/admin/usePortvilleRoomAuth';
import { setAdminScrollTop } from '../../../utils/adminScroll';
import { useBackToPortvilleRoomsListOnPop } from '../../../hooks/useBackToPortvilleRoomsListOnPop';
import { markPortvilleRoomsRestoreOnNext } from '../../../utils/portvilleRoomsScroll';

type ContractTab = 'original' | 'extension1' | 'extension2';

const ViewContractPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const contractTypeParam = searchParams.get('type');
  const { loading: authLoading, hasAccess } = usePortvilleRoomAuth();
  const contractRef = useRef<HTMLDivElement>(null);
  
  const [room, setRoom] = useState<PortvilleRoomWithResidency | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const normalizeTab = (type: string | null): ContractTab => {
    if (type === 'extension2') return 'extension2';
    if (type === 'extension1' || type === 'extension') return 'extension1'; // legacy 'extension' = 1차
    return 'original';
  };

  const [tab, setTab] = useState<ContractTab>(normalizeTab(contractTypeParam));
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const cachedFontRef = useRef<{ normal?: string; bold?: string }>({});

  const goToRoomsList = () => {
    markPortvilleRoomsRestoreOnNext();
    navigate('/admin/portville-rooms', { state: { restoreScroll: true } });
  };

  useBackToPortvilleRoomsListOnPop();

  useEffect(() => {
    setAdminScrollTop(0);
  }, []);

  useEffect(() => {
    if (roomId && hasAccess) {
      loadRoom();
    }
  }, [roomId, hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTab(normalizeTab(contractTypeParam));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractTypeParam]);

  const loadRoom = async () => {
    if (!roomId) return;
    
    try {
      setLoading(true);
      
      // 1. 호실 정보 조회
      const roomDoc = await getDoc(doc(db, 'portville_rooms', roomId));
      if (!roomDoc.exists()) {
        setError('호실을 찾을 수 없습니다.');
        return;
      }

      const roomData = roomDoc.data() as PortvilleRoom;
      
      // 2. 현재 입주 기록 조회
      let currentResidency: PortvilleResidency | undefined;
      if (roomData.currentResidencyId) {
        const residencyDoc = await getDoc(doc(db, 'portville_residencies', roomData.currentResidencyId));
        if (residencyDoc.exists()) {
          const residencyData = residencyDoc.data();
          currentResidency = {
            id: residencyDoc.id,
            roomId: residencyData.roomId,
            roomNumber: residencyData.roomNumber,
            residentUid: residencyData.residentUid,
            residentInfo: residencyData.residentInfo,
            occupancyPeriod: residencyData.occupancyPeriod ? {
              startDate: residencyData.occupancyPeriod.startDate.toDate(),
              endDate: residencyData.occupancyPeriod.endDate.toDate()
            } : undefined,
            roommateInfo: residencyData.roommateInfo,
            vehicleInfo: residencyData.vehicleInfo,
            notes: residencyData.notes,
            signatureName: residencyData.signatureName,
            signatureAddress: residencyData.signatureAddress,
            signatureResidentNumber: residencyData.signatureResidentNumber,
            signatureDate: residencyData.signatureDate ? residencyData.signatureDate.toDate() : undefined,
            signatureUrl: residencyData.signatureUrl,
            contractSignedAt: residencyData.contractSignedAt ? residencyData.contractSignedAt.toDate() : undefined,
            moveInInspectionSignedAt: residencyData.moveInInspectionSignedAt ? residencyData.moveInInspectionSignedAt.toDate() : undefined,
            moveInInspectionSignatureUrl: residencyData.moveInInspectionSignatureUrl,
            landlordSignedContractUrl: residencyData.landlordSignedContractUrl,
            landlordSignedContractUploadedAt: residencyData.landlordSignedContractUploadedAt ? residencyData.landlordSignedContractUploadedAt.toDate() : undefined,
            contractContent: residencyData.contractContent,
            // 연장계약용 항만공사 날인본 필드 추가
            extensionLandlordSignedContractUrl: residencyData.extensionLandlordSignedContractUrl,
            extensionLandlordSignedContractUploadedAt: residencyData.extensionLandlordSignedContractUploadedAt ? residencyData.extensionLandlordSignedContractUploadedAt.toDate() : undefined,
            // 계약 연장 관련 정보 추가
            hasExtended: residencyData.hasExtended,
            extensionStatus: residencyData.extensionStatus,
            extensionAppliedAt: residencyData.extensionAppliedAt ? residencyData.extensionAppliedAt.toDate() : undefined,
            extensionApprovedAt: residencyData.extensionApprovedAt ? residencyData.extensionApprovedAt.toDate() : undefined,
            extensionRejectedAt: residencyData.extensionRejectedAt ? residencyData.extensionRejectedAt.toDate() : undefined,
            extensionRejectionReason: residencyData.extensionRejectionReason,
            originalEndDate: residencyData.originalEndDate ? residencyData.originalEndDate.toDate() : undefined,
            extensionSignatureName: residencyData.extensionSignatureName,
            extensionSignatureDate: residencyData.extensionSignatureDate ? residencyData.extensionSignatureDate.toDate() : undefined,
            extensionSignatureUrl: residencyData.extensionSignatureUrl,
            extensionContractSignedAt: residencyData.extensionContractSignedAt ? residencyData.extensionContractSignedAt.toDate() : undefined,
            extensionContractContent: residencyData.extensionContractContent,
            // 2차 연장계약용 항만공사 날인본 필드 추가
            secondExtensionLandlordSignedContractUrl: residencyData.secondExtensionLandlordSignedContractUrl,
            secondExtensionLandlordSignedContractUploadedAt: residencyData.secondExtensionLandlordSignedContractUploadedAt
              ? residencyData.secondExtensionLandlordSignedContractUploadedAt.toDate()
              : undefined,
            // 2차 계약 연장 관련 정보 추가
            hasSecondExtended: residencyData.hasSecondExtended,
            secondExtensionStatus: residencyData.secondExtensionStatus,
            secondExtensionAppliedAt: residencyData.secondExtensionAppliedAt ? residencyData.secondExtensionAppliedAt.toDate() : undefined,
            secondExtensionApprovedAt: residencyData.secondExtensionApprovedAt ? residencyData.secondExtensionApprovedAt.toDate() : undefined,
            secondExtensionRejectedAt: residencyData.secondExtensionRejectedAt ? residencyData.secondExtensionRejectedAt.toDate() : undefined,
            secondExtensionRejectionReason: residencyData.secondExtensionRejectionReason,
            secondOriginalEndDate: residencyData.secondOriginalEndDate ? residencyData.secondOriginalEndDate.toDate() : undefined,
            secondExtensionSignatureName: residencyData.secondExtensionSignatureName,
            secondExtensionSignatureDate: residencyData.secondExtensionSignatureDate ? residencyData.secondExtensionSignatureDate.toDate() : undefined,
            secondExtensionSignatureUrl: residencyData.secondExtensionSignatureUrl,
            secondExtensionContractSignedAt: residencyData.secondExtensionContractSignedAt ? residencyData.secondExtensionContractSignedAt.toDate() : undefined,
            secondExtensionContractContent: residencyData.secondExtensionContractContent,
            status: residencyData.status,
            createdAt: residencyData.createdAt.toDate(),
            updatedAt: residencyData.updatedAt.toDate(),
            completedAt: residencyData.completedAt ? residencyData.completedAt.toDate() : undefined
          };
        }
      }

      // 3. 통합 뷰 생성
      const combinedRoom: PortvilleRoomWithResidency = {
          id: roomDoc.id,
        roomNumber: roomData.roomNumber,
        roomType: roomData.roomType || '호실',
        status: roomData.status,
        createdAt: (roomData.createdAt as any).toDate ? (roomData.createdAt as any).toDate() : roomData.createdAt as Date,
        updatedAt: (roomData.updatedAt as any).toDate ? (roomData.updatedAt as any).toDate() : roomData.updatedAt as Date,
        currentResidencyId: roomData.currentResidencyId,
        facilityInfo: roomData.facilityInfo
      };

      if (currentResidency) {
        combinedRoom.currentResident = currentResidency.residentInfo;
        combinedRoom.residentUid = currentResidency.residentUid;
        combinedRoom.occupancyPeriod = currentResidency.occupancyPeriod;
        combinedRoom.roommateInfo = currentResidency.roommateInfo;
        combinedRoom.vehicleInfo = currentResidency.vehicleInfo;
        combinedRoom.notes = currentResidency.notes;
        combinedRoom.signatureName = currentResidency.signatureName;
        combinedRoom.signatureAddress = currentResidency.signatureAddress;
        combinedRoom.signatureResidentNumber = currentResidency.signatureResidentNumber;
        combinedRoom.signatureDate = currentResidency.signatureDate;
        combinedRoom.signatureUrl = currentResidency.signatureUrl;
        combinedRoom.contractSignedAt = currentResidency.contractSignedAt;
        combinedRoom.moveInInspectionSignedAt = currentResidency.moveInInspectionSignedAt;
        combinedRoom.moveInInspectionSignatureUrl = currentResidency.moveInInspectionSignatureUrl;
        combinedRoom.landlordSignedContractUrl = currentResidency.landlordSignedContractUrl;
        combinedRoom.landlordSignedContractUploadedAt = currentResidency.landlordSignedContractUploadedAt;
        combinedRoom.contractContent = currentResidency.contractContent;
        // 계약 연장 관련 정보 추가
        combinedRoom.hasExtended = currentResidency.hasExtended;
        combinedRoom.extensionStatus = currentResidency.extensionStatus;
        combinedRoom.extensionAppliedAt = currentResidency.extensionAppliedAt;
        combinedRoom.extensionApprovedAt = currentResidency.extensionApprovedAt;
        combinedRoom.extensionRejectedAt = currentResidency.extensionRejectedAt;
        combinedRoom.extensionRejectionReason = currentResidency.extensionRejectionReason;
        combinedRoom.originalEndDate = currentResidency.originalEndDate;
        combinedRoom.extensionSignatureName = currentResidency.extensionSignatureName;
        combinedRoom.extensionSignatureDate = currentResidency.extensionSignatureDate;
        combinedRoom.extensionSignatureUrl = currentResidency.extensionSignatureUrl;
        combinedRoom.extensionContractSignedAt = currentResidency.extensionContractSignedAt;
        combinedRoom.extensionLandlordSignedContractUrl = currentResidency.extensionLandlordSignedContractUrl;
        combinedRoom.extensionLandlordSignedContractUploadedAt = currentResidency.extensionLandlordSignedContractUploadedAt;
        combinedRoom.extensionContractContent = currentResidency.extensionContractContent;

        // 2차 계약 연장 관련 정보 추가
        combinedRoom.hasSecondExtended = currentResidency.hasSecondExtended;
        combinedRoom.secondExtensionStatus = currentResidency.secondExtensionStatus;
        combinedRoom.secondExtensionAppliedAt = currentResidency.secondExtensionAppliedAt;
        combinedRoom.secondExtensionApprovedAt = currentResidency.secondExtensionApprovedAt;
        combinedRoom.secondExtensionRejectedAt = currentResidency.secondExtensionRejectedAt;
        combinedRoom.secondExtensionRejectionReason = currentResidency.secondExtensionRejectionReason;
        combinedRoom.secondOriginalEndDate = currentResidency.secondOriginalEndDate;
        combinedRoom.secondExtensionSignatureName = currentResidency.secondExtensionSignatureName;
        combinedRoom.secondExtensionSignatureDate = currentResidency.secondExtensionSignatureDate;
        combinedRoom.secondExtensionSignatureUrl = currentResidency.secondExtensionSignatureUrl;
        combinedRoom.secondExtensionContractSignedAt = currentResidency.secondExtensionContractSignedAt;
        combinedRoom.secondExtensionLandlordSignedContractUrl = currentResidency.secondExtensionLandlordSignedContractUrl;
        combinedRoom.secondExtensionLandlordSignedContractUploadedAt =
          currentResidency.secondExtensionLandlordSignedContractUploadedAt;
        combinedRoom.secondExtensionContractContent = currentResidency.secondExtensionContractContent;
      }
        
      setRoom(combinedRoom);
        
      // 기본 검증: 일반 계약이 완료되지 않은 경우에는 이 페이지에서 볼 것이 없음
      if (!combinedRoom.contractSignedAt) {
        setError('아직 계약이 완료되지 않은 호실입니다.');
        return;
      }

      // 존재하지 않는 탭을 쿼리로 강제한 경우, 가능한 탭으로 되돌린다.
      const hasExtension1 = Boolean(combinedRoom.extensionContractSignedAt);
      const hasExtension2 = Boolean(combinedRoom.secondExtensionContractSignedAt);

      if (tab === 'extension2' && !hasExtension2) {
        const fallback: ContractTab = hasExtension1 ? 'extension1' : 'original';
        setTab(fallback);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('type', fallback);
          return next;
        });
      } else if (tab === 'extension1' && !hasExtension1) {
        setTab('original');
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('type', 'original');
          return next;
        });
      }
    } catch (error) {
      console.error('Error loading room:', error);
      setError('호실 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // PDF 다운로드 함수
  const downloadContractPdf = async () => {
    if (!room) return;

    try {
      setGeneratingPdf(true);

      const loadFontBase64 = async (url: string) => {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`폰트 로드 실패: ${response.status}`);
        const buffer = await response.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const chunkSize = 0x8000;
        const chunks: string[] = [];
        for (let index = 0; index < bytes.length; index += chunkSize) {
          const chunk = bytes.subarray(index, index + chunkSize);
          chunks.push(String.fromCharCode(...chunk));
        }
        return btoa(chunks.join(''));
      };

      const ensureKoreanFont = async (pdf: jsPDF) => {
        if (!cachedFontRef.current.normal) {
          const fontUrl = `${process.env.PUBLIC_URL}/fonts/NotoSansKR-Regular.ttf`;
          cachedFontRef.current.normal = await loadFontBase64(fontUrl);
        }
        if (!cachedFontRef.current.bold) {
          const fontUrl = `${process.env.PUBLIC_URL}/fonts/NotoSansKR-Bold.ttf`;
          cachedFontRef.current.bold = await loadFontBase64(fontUrl);
        }
        pdf.addFileToVFS('NotoSansKR-Regular.ttf', cachedFontRef.current.normal);
        pdf.addFileToVFS('NotoSansKR-Bold.ttf', cachedFontRef.current.bold);
        pdf.addFont('NotoSansKR-Regular.ttf', 'NotoSansKR', 'normal');
        pdf.addFont('NotoSansKR-Bold.ttf', 'NotoSansKR', 'bold');
        pdf.setFont('NotoSansKR', 'normal');
      };

      const fetchAsDataUrl = async (url: string) => {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) throw new Error(`이미지 로드 실패: ${response.status}`);
        const blob = await response.blob();
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = () => reject(new Error('이미지 변환 실패'));
          reader.onload = () => resolve(String(reader.result));
          reader.readAsDataURL(blob);
        });
      };

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      await ensureKoreanFont(pdf);

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const marginTop = 12;
      const marginBottom = 15;
      const marginLeft = 12;
      const marginRight = 12;
      const contentWidth = pdfWidth - marginLeft - marginRight;
      const contentBottomY = pdfHeight - marginBottom;

      const lineHeightFactor = 1.45;
      const lineHeightMm = (fontSize: number) => (fontSize * lineHeightFactor) / pdf.internal.scaleFactor;

      const ensureSpace = (currentY: number, neededHeight: number) => {
        if (currentY + neededHeight <= contentBottomY) return currentY;
        pdf.addPage();
        return marginTop;
      };

      const wrapTextPreservingNewlines = (text: string, maxWidthMm: number, fontSize: number) => {
        pdf.setFontSize(fontSize);
        const parts = text.replace(/\r\n/g, '\n').split('\n');
        const lines: string[] = [];
        for (const part of parts) {
          if (part.trim() === '') {
            lines.push('');
            continue;
          }
          const wrapped = pdf.splitTextToSize(part, maxWidthMm) as string[];
          lines.push(...wrapped);
        }
        return lines;
      };

      const drawLines = (lines: string[], x: number, startY: number, fontSize: number) => {
        pdf.setFontSize(fontSize);
        let y = startY;
        const lh = lineHeightMm(fontSize);
        for (const line of lines) {
          y = ensureSpace(y, lh);
          if (line === '') {
            y += lh;
            continue;
          }
          pdf.text(line, x, y);
          y += lh;
        }
        return y;
      };

      const drawSectionText = (label: string | null, text: string, fontSize = 10) => {
        let y = cursorYRef.current;
        if (label) {
          pdf.setFont('NotoSansKR', 'bold');
          pdf.setFontSize(11);
          y = ensureSpace(y, lineHeightMm(11));
          pdf.text(label, marginLeft, y);
          y += lineHeightMm(11);
          pdf.setFont('NotoSansKR', 'normal');
        }
        const lines = wrapTextPreservingNewlines(text, contentWidth, fontSize);
        y = drawLines(lines, marginLeft, y, fontSize);
        cursorYRef.current = y + 2;
      };

      const cursorYRef = { current: marginTop };

      const sanitizePdfText = (text: string) =>
        text
          .replace(/\r\n/g, '\n')
          .replace(/\t/g, ' ')
          .replace(/\u00a0/g, ' ');

      const normalizeLineBreaks = (text: string) => sanitizePdfText(text).replace(/\n\n+/g, '\n').trim();

      const contractContent =
        tab === 'extension2'
          ? room.secondExtensionContractContent
          : tab === 'extension1'
            ? room.extensionContractContent
            : room.contractContent;
      if (!contractContent) throw new Error('계약서 내용을 찾을 수 없습니다.');

      pdf.setFont('NotoSansKR', 'bold');
      pdf.setFontSize(16);
      const titleLabel =
        tab === 'extension2' ? '포트빌 입주 2차 연장계약서' : tab === 'extension1' ? '포트빌 입주 연장계약서' : '포트빌 입주 계약서';
      pdf.text(titleLabel, pdfWidth / 2, cursorYRef.current, {
        align: 'center'
      });
      cursorYRef.current += lineHeightMm(16) + 2;
      pdf.setFont('NotoSansKR', 'normal');

      const writeArticle = (raw: string) => {
        const normalized = normalizeLineBreaks(raw);
        const [firstLine, ...rest] = normalized.split('\n');
        if (firstLine?.trim()) {
          pdf.setFont('NotoSansKR', 'bold');
          const firstLines = wrapTextPreservingNewlines(firstLine, contentWidth, 11);
          cursorYRef.current = drawLines(firstLines, marginLeft, cursorYRef.current, 11);
          pdf.setFont('NotoSansKR', 'normal');
        }
        if (rest.length) {
          const restText = rest.join('\n').trim();
          if (restText) {
            const restLines = wrapTextPreservingNewlines(restText, contentWidth, 10);
            cursorYRef.current = drawLines(restLines, marginLeft, cursorYRef.current, 10);
          }
        }
        cursorYRef.current += 3;
      };

      if (contractContent.header) drawSectionText(null, normalizeLineBreaks(contractContent.header), 10);
      const articles = [
        contractContent.article1,
        contractContent.article2,
        contractContent.article3,
        contractContent.article4,
        contractContent.article5,
        contractContent.article6,
        contractContent.article7,
        contractContent.article8,
        contractContent.article9,
        contractContent.article10,
        contractContent.article11
      ].filter((v): v is string => Boolean(v));

      for (const article of articles) writeArticle(sanitizePdfText(article));

      if (contractContent.footer) {
        const footerLines = wrapTextPreservingNewlines(normalizeLineBreaks(contractContent.footer), contentWidth, 11);
        cursorYRef.current = ensureSpace(cursorYRef.current, footerLines.length * lineHeightMm(11) + 4);
        pdf.setFont('NotoSansKR', 'bold');
        pdf.setFontSize(11);
        for (const line of footerLines) {
          if (line === '') {
            cursorYRef.current += lineHeightMm(11);
            continue;
          }
          pdf.text(line, pdfWidth / 2, cursorYRef.current, { align: 'center' });
          cursorYRef.current += lineHeightMm(11);
        }
        pdf.setFont('NotoSansKR', 'normal');
        cursorYRef.current += 2;
      }

      const signDate =
        tab === 'extension2'
          ? room.secondExtensionSignatureDate?.toLocaleDateString('ko-KR')
          : tab === 'extension1'
            ? room.extensionSignatureDate?.toLocaleDateString('ko-KR')
            : room.signatureDate?.toLocaleDateString('ko-KR');
      if (signDate) {
        cursorYRef.current = ensureSpace(cursorYRef.current, lineHeightMm(11) + 4);
        pdf.setFont('NotoSansKR', 'bold');
        pdf.setFontSize(11);
        pdf.text(signDate, pdfWidth / 2, cursorYRef.current, { align: 'center' });
        cursorYRef.current += lineHeightMm(11) + 6;
        pdf.setFont('NotoSansKR', 'normal');
      }

      // 서명/날인 영역 (2열)
      const gap = 8;
      const columnWidth = (contentWidth - gap) / 2;
      const leftX = marginLeft;
      const rightX = marginLeft + columnWidth + gap;

      const tenantName =
        tab === 'extension2'
          ? room.secondExtensionSignatureName
          : tab === 'extension1'
            ? room.extensionSignatureName
            : room.signatureName;
      const signerInfo = contractContent.signerInfo;
      const tenantAddress = signerInfo?.address || (room as any).signatureAddress || '-';
      const tenantResidentNumber = signerInfo?.residentNumber || (room as any).signatureResidentNumber || '-';

      const leftInfoLines = [
        ...wrapTextPreservingNewlines(`임차인: ${tenantName || '-'}`, columnWidth, 10),
        ...wrapTextPreservingNewlines(`주소: ${tenantAddress}`, columnWidth, 10),
        ...wrapTextPreservingNewlines(`주민등록번호: ${tenantResidentNumber}`, columnWidth, 10)
      ];
      const rightInfoLines = [
        ...wrapTextPreservingNewlines('임대인: 부산항만공사 (602-81-31628)', columnWidth, 10),
        ...wrapTextPreservingNewlines('사장 송상근', columnWidth, 10),
        ...wrapTextPreservingNewlines('부산광역시 중구 대교로 122', columnWidth, 10)
      ];

      const infoFontSize = 10;
      const infoHeight = Math.max(leftInfoLines.length, rightInfoLines.length) * lineHeightMm(infoFontSize) + 6;
      const boxHeight = 42;
      const needed = infoHeight + boxHeight + 6;
      cursorYRef.current = ensureSpace(cursorYRef.current, needed);

      pdf.setFontSize(12);
      pdf.setFont('NotoSansKR', 'bold');
      pdf.text('임차인 서명', leftX, cursorYRef.current);
      pdf.text('임대인 날인', rightX, cursorYRef.current);
      cursorYRef.current += lineHeightMm(12) + 2;
      pdf.setFont('NotoSansKR', 'normal');

      const infoStartY = cursorYRef.current;
      drawLines(leftInfoLines, leftX, infoStartY, infoFontSize);
      drawLines(rightInfoLines, rightX, infoStartY, infoFontSize);
      cursorYRef.current = infoStartY + infoHeight;

      const drawBox = (x: number, y: number, w: number, h: number) => {
        pdf.setDrawColor(180);
        pdf.setLineWidth(0.3);
        const maybeSetLineDash = (pdf as any).setLineDash as ((pattern: number[], phase: number) => void) | undefined;
        maybeSetLineDash?.call(pdf, [1.2, 1.2], 0);
        pdf.rect(x, y, w, h);
        maybeSetLineDash?.call(pdf, [], 0);
      };

      const boxY = cursorYRef.current;
      drawBox(leftX, boxY, columnWidth, boxHeight);
      drawBox(rightX, boxY, columnWidth, boxHeight);

      // 임차인 서명 이미지 삽입 (가능한 경우에만)
      const signatureUrl =
        tab === 'extension2'
          ? room.secondExtensionSignatureUrl
          : tab === 'extension1'
            ? room.extensionSignatureUrl
            : room.signatureUrl;
      if (signatureUrl) {
        try {
          const dataUrl = await fetchAsDataUrl(signatureUrl);
          const pad = 3;
          pdf.addImage(dataUrl, 'PNG', leftX + pad, boxY + pad, columnWidth - pad * 2, boxHeight - pad * 2, undefined, 'FAST');
        } catch (e) {
          pdf.setFontSize(10);
          pdf.text('서명 이미지를 불러올 수 없습니다.', leftX + 4, boxY + 10);
        }
      } else {
        pdf.setFontSize(10);
        pdf.text('서명 이미지 없음', leftX + 4, boxY + 10);
      }

      // 임대인 날인 안내 텍스트
      pdf.setTextColor(120);
      pdf.setFontSize(10);
      pdf.text('부산항만공사', rightX + 4, boxY + 14);
      pdf.text('(직인)', rightX + 4, boxY + 20);
      pdf.setTextColor(0);

      // PDF 다운로드 - 연장계약인지 일반계약인지 구분하여 파일명 생성
      const contractTypeLabel = tab === 'extension2' ? '2차연장계약서' : tab === 'extension1' ? '연장계약서' : '계약서';
      pdf.save(`포트빌_${room.roomNumber}호_${contractTypeLabel}_${new Date().toISOString().split('T')[0]}.pdf`);

    } catch (error) {
      console.error('PDF 생성 오류:', error);
      alert('PDF 생성 중 오류가 발생했습니다.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // 파일 선택 핸들러
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('PDF 파일만 업로드 가능합니다.');
        return;
      }
      setUploadedFile(file);
    }
  };

  // 임대인 날인본 업로드 함수
  const uploadLandlordSignedContract = async () => {
    if (!uploadedFile || !room || !roomId) {
      alert('업로드에 필요한 정보가 부족합니다.');
      return;
    }
    
    try {
      setUploading(true);
      
      // Firebase Storage에 파일 업로드
      // 파일명 패턴: {residentUid}_{roomId}_{type}_{timestamp}.pdf
      const timestamp = new Date().getTime();
      const fileOwnerKey = room.residentUid || roomId;
      const fileName = `landlord_signed_contracts/${fileOwnerKey}_${roomId}_${tab}_${timestamp}.pdf`;
      const storageRef = ref(storage, fileName);
      
      const snapshot = await uploadBytes(storageRef, uploadedFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Firestore에 URL 정보 업데이트 - 계약 타입에 따라 다른 필드 사용
      const updateData: any = {
        updatedAt: new Date()
      };
      
      if (tab === 'extension1') {
        updateData.extensionLandlordSignedContractUrl = downloadURL;
        updateData.extensionLandlordSignedContractUploadedAt = new Date();
      } else if (tab === 'extension2') {
        updateData.secondExtensionLandlordSignedContractUrl = downloadURL;
        updateData.secondExtensionLandlordSignedContractUploadedAt = new Date();
      } else {
        updateData.landlordSignedContractUrl = downloadURL;
        updateData.landlordSignedContractUploadedAt = new Date();
      }
      
      await updateDoc(doc(db, 'portville_residencies', room.currentResidencyId!), updateData);
      
      // 상태 업데이트
      setRoom(prev => prev ? {
        ...prev,
        ...(tab === 'extension1'
          ? {
              extensionLandlordSignedContractUrl: downloadURL,
              extensionLandlordSignedContractUploadedAt: new Date()
            }
          : tab === 'extension2'
            ? {
                secondExtensionLandlordSignedContractUrl: downloadURL,
                secondExtensionLandlordSignedContractUploadedAt: new Date()
              }
            : {
                landlordSignedContractUrl: downloadURL,
                landlordSignedContractUploadedAt: new Date()
              })
      } : null);
      
      setUploadedFile(null);
      // 파일 입력 초기화
      const fileInput = document.getElementById('landlord-contract-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      alert(
        `항만공사 ${
          tab === 'extension2' ? '2차 연장' : tab === 'extension1' ? '연장' : ''
        }계약 날인본이 성공적으로 업로드되었습니다.`
      );
      
    } catch (error) {
      console.error('파일 업로드 오류:', error);
      alert('파일 업로드 중 오류가 발생했습니다.');
    } finally {
      setUploading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">로딩 중...</div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">계약 정보를 불러오는 중...</div>
      </div>
    );
  }

  if (error || !room) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error || '호실을 찾을 수 없습니다.'}</div>
          <button
            onClick={goToRoomsList}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            호실 관리로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
	    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
	      <div className="mb-8">
	        <h1 className="text-3xl font-bold text-gray-900">
	          {tab === 'extension2' ? '2차 연장' : tab === 'extension1' ? '연장' : ''}계약서 확인
	        </h1>
	        <p className="mt-2 text-sm text-gray-600">
	          {room.roomNumber}호 입주
	          {tab === 'extension2' ? ' 2차 연장' : tab === 'extension1' ? ' 연장' : ''}계약서 내용 및 서명을 확인할 수 있습니다.
	        </p>
	        {tab === 'extension1' && room.originalEndDate && (
	          <p className="mt-1 text-sm text-blue-600">
	            원래 계약 종료일: {room.originalEndDate.toLocaleDateString('ko-KR')}
	          </p>
	        )}
	        {tab === 'extension2' && room.secondOriginalEndDate && (
	          <p className="mt-1 text-sm text-blue-600">
	            직전(1차 연장 후) 계약 종료일: {room.secondOriginalEndDate.toLocaleDateString('ko-KR')}
	          </p>
	        )}

	        {/* 탭 (연장계약 체결 시 노출) */}
	        {(room.extensionContractSignedAt || room.secondExtensionContractSignedAt) && (
	          <div className="mt-4 inline-flex rounded-md border border-gray-200 overflow-hidden">
	            <button
	              onClick={() => {
	                setTab('original');
	                setSearchParams((prev) => {
	                  const next = new URLSearchParams(prev);
	                  next.set('type', 'original');
	                  return next;
	                });
	              }}
	              className={`px-4 py-2 text-sm font-medium ${
	                tab === 'original' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
	              }`}
	            >
	              계약서
	            </button>
	            {room.extensionContractSignedAt && (
	              <button
	                onClick={() => {
	                  setTab('extension1');
	                  setSearchParams((prev) => {
	                    const next = new URLSearchParams(prev);
	                    next.set('type', 'extension1');
	                    return next;
	                  });
	                }}
	                className={`px-4 py-2 text-sm font-medium border-l border-gray-200 ${
	                  tab === 'extension1' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
	                }`}
	              >
	                1차 연장계약서
	              </button>
	            )}
	            {room.secondExtensionContractSignedAt && (
	              <button
	                onClick={() => {
	                  setTab('extension2');
	                  setSearchParams((prev) => {
	                    const next = new URLSearchParams(prev);
	                    next.set('type', 'extension2');
	                    return next;
	                  });
	                }}
	                className={`px-4 py-2 text-sm font-medium border-l border-gray-200 ${
	                  tab === 'extension2' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
	                }`}
	              >
	                2차 연장계약서
	              </button>
	            )}
	          </div>
	        )}
	      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
	        {/* 계약 완료 상태 표시 */}
	        {(() => {
	          const isExtension1 = tab === 'extension1';
	          const isExtension2 = tab === 'extension2';
	          const isExtensionContract = tab !== 'original';
	          const completedAt = isExtension2
	            ? room.secondExtensionContractSignedAt
	            : isExtension1
	              ? room.extensionContractSignedAt
	              : room.contractSignedAt;
	          const isContractCompleted = Boolean(completedAt);
	          const contractTypeLabel = isExtension2 ? '2차 연장계약' : isExtension1 ? '연장계약' : '계약';
	          const bgColor = isExtension2
	            ? 'bg-purple-50 border-purple-400'
	            : isExtension1
	              ? 'bg-blue-50 border-blue-400'
	              : 'bg-green-50 border-green-400';
	          const iconColor = isExtension2
	            ? 'text-purple-400'
	            : isExtension1
	              ? 'text-blue-400'
	              : 'text-green-400';
	          const textColor = isExtension2
	            ? 'text-purple-700'
	            : isExtension1
	              ? 'text-blue-700'
	              : 'text-green-700';

	          return isContractCompleted && (
	            <div className={`${bgColor} border-l-4 p-4 mb-6`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className={`h-5 w-5 ${iconColor}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
	                <div className="ml-3">
	                  <p className={`text-sm ${textColor}`}>
	                    <strong>{contractTypeLabel} 완료됨</strong> - {completedAt?.toLocaleString('ko-KR')}
	                  </p>
	                  {isExtension1 && room.originalEndDate && (
	                    <p className="text-xs text-blue-600 mt-1">
	                      원래 계약 종료일: {room.originalEndDate.toLocaleDateString('ko-KR')}
	                    </p>
	                  )}
	                  {isExtension2 && room.secondOriginalEndDate && (
	                    <p className="text-xs text-purple-600 mt-1">
	                      직전(1차 연장 후) 계약 종료일: {room.secondOriginalEndDate.toLocaleDateString('ko-KR')}
	                    </p>
	                  )}
	                </div>
	              </div>
	            </div>
	          );
	        })()}

        {/* 액션 버튼들 */}
        <div className="px-8 pt-6 pb-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="flex gap-4">
              <button
                onClick={downloadContractPdf}
                disabled={generatingPdf}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {generatingPdf ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    PDF 생성 중...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    계약서 PDF 다운로드
                  </>
                )}
              </button>
            </div>
            
            {/* 임대인 날인본 상태 */}
	            <div className="text-sm">
	              {(() => {
	                // 계약 타입에 따라 적절한 필드 사용
	                const landlordContractUrl =
	                  tab === 'extension2'
	                    ? room.secondExtensionLandlordSignedContractUrl
	                    : tab === 'extension1'
	                      ? room.extensionLandlordSignedContractUrl
	                      : room.landlordSignedContractUrl;
	                const landlordContractUploadedAt =
	                  tab === 'extension2'
	                    ? room.secondExtensionLandlordSignedContractUploadedAt
	                    : tab === 'extension1'
	                      ? room.extensionLandlordSignedContractUploadedAt
	                      : room.landlordSignedContractUploadedAt;
	                
	                return landlordContractUrl ? (
	                  <div className="flex items-center gap-2 text-green-600">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
	                    항만공사 {tab === 'extension2' ? '2차 연장' : tab === 'extension1' ? '연장' : ''}계약 날인본 등록됨 (
	                    {landlordContractUploadedAt?.toLocaleDateString('ko-KR')})
	                  </div>
	                ) : (
	                  <div className="text-orange-600">
	                    항만공사 {tab === 'extension2' ? '2차 연장' : tab === 'extension1' ? '연장' : ''}계약 날인본 미등록
	                  </div>
	                );
	              })()}
	            </div>
          </div>
        </div>

        {/* 계약서 내용 (PDF 생성 대상만) */}
        <div className="p-8">
          <div ref={contractRef} className="bg-white">
	          <div className="text-center mb-8">
	            <h2 className="text-2xl font-bold text-gray-900">
	              {tab === 'extension2'
	                ? '포트빌 입주 2차 연장계약서'
	                : tab === 'extension1'
	                  ? '포트빌 입주 연장계약서'
	                  : '포트빌 입주 계약서'}
	            </h2>
	            {tab === 'extension1' && room.originalEndDate && (
	              <p className="text-sm text-blue-600 mt-1">
	                (원래 계약 종료일: {room.originalEndDate.toLocaleDateString('ko-KR')})
	              </p>
	            )}
	            {tab === 'extension2' && room.secondOriginalEndDate && (
	              <p className="text-sm text-blue-600 mt-1">
	                (직전(1차 연장 후) 계약 종료일: {room.secondOriginalEndDate.toLocaleDateString('ko-KR')})
	              </p>
	            )}
	          </div>

          <div className="space-y-6 text-sm">
            <style>{`
              .contract-article {
                white-space: pre-line;
                line-height: 1.75;
              }
              .contract-article::first-line {
                font-weight: 600;
                color: #111827;
                font-size: 0.9375rem;
              }
              /* PDF 페이지 분할을 위한 스타일 */
              .pdf-section {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                -webkit-column-break-inside: avoid !important;
                padding-bottom: 10px;
                margin-bottom: 10px;
              }
              .pdf-page-break {
                page-break-after: always !important;
                break-after: page !important;
              }
              /* 서명 이미지 최적화 */
              .signature-image {
                max-width: 100%;
                height: auto;
                display: block;
              }
            `}</style>
	            {(() => {
	              // 연장계약인지 일반계약인지 판단하여 해당 계약 내용 사용
	              const contractContent =
	                tab === 'extension2'
	                  ? room.secondExtensionContractContent
	                  : tab === 'extension1' && room.extensionContractContent
	                    ? room.extensionContractContent
	                    : room.contractContent;

              // 연속된 줄바꿈을 하나로 정규화하는 함수
              const normalizeLineBreaks = (text: string) => {
                return text.replace(/\n\n+/g, '\n');
              };

              return (
                <>
                  {/* 헤더 정보 */}
                  {contractContent?.header && (
                    <div className="pdf-section">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {normalizeLineBreaks(contractContent.header)}
                      </p>
                    </div>
                  )}

                  {/* 제1조 */}
                  {contractContent?.article1 && (
                    <div className="pdf-section">
                      <p className="contract-article text-gray-700">
                        {normalizeLineBreaks(contractContent.article1)}
                      </p>
                    </div>
                  )}

                  {/* 제2조 */}
                  {contractContent?.article2 && (
                    <div className="pdf-section">
                      <p className="contract-article text-gray-700">
                        {normalizeLineBreaks(contractContent.article2)}
                      </p>
                    </div>
                  )}

                  {/* 제3조 */}
                  {contractContent?.article3 && (
                    <div className="pdf-section">
                      <p className="contract-article text-gray-700">
                        {normalizeLineBreaks(contractContent.article3)}
                      </p>
                    </div>
                  )}

                  {/* 제4조 */}
                  {contractContent?.article4 && (
                    <div className="pdf-section">
                      <p className="contract-article text-gray-700">
                        {normalizeLineBreaks(contractContent.article4)}
                      </p>
                    </div>
                  )}

                  {/* 제5조 */}
                  {contractContent?.article5 && (
                    <div className="pdf-section">
                      <p className="contract-article text-gray-700">
                        {normalizeLineBreaks(contractContent.article5)}
                      </p>
                    </div>
                  )}

                  {/* 제6조 */}
                  {contractContent?.article6 && (
                    <div className="pdf-section">
                      <p className="contract-article text-gray-700">
                        {normalizeLineBreaks(contractContent.article6)}
                      </p>
                    </div>
                  )}

                  {/* 제7조 */}
                  {contractContent?.article7 && (
                    <div className="pdf-section">
                      <p className="contract-article text-gray-700">
                        {normalizeLineBreaks(contractContent.article7)}
                      </p>
                    </div>
                  )}

                  {/* 제8조 */}
                  {contractContent?.article8 && (
                    <div className="pdf-section">
                      <p className="contract-article text-gray-700">
                        {normalizeLineBreaks(contractContent.article8)}
                      </p>
                    </div>
                  )}

                  {/* 제9조 */}
                  {contractContent?.article9 && (
                    <div className="pdf-section">
                      <p className="contract-article text-gray-700">
                        {normalizeLineBreaks(contractContent.article9)}
                      </p>
                    </div>
                  )}

                  {/* 제10조 */}
                  {contractContent?.article10 && (
                    <div className="pdf-section">
                      <p className="contract-article text-gray-700">
                        {normalizeLineBreaks(contractContent.article10)}
                      </p>
                    </div>
                  )}

                  {/* 제11조 */}
                  {contractContent?.article11 && (
                    <div className="pdf-section">
                      <p className="contract-article text-gray-700">
                        {normalizeLineBreaks(contractContent.article11)}
                      </p>
                    </div>
                  )}

                  {/* 푸터 정보 */}
                  {contractContent?.footer && (
                    <div className="pdf-section text-center">
                      <p className="font-semibold text-gray-900 whitespace-pre-line">
                        {normalizeLineBreaks(contractContent.footer)}
                      </p>
                    </div>
                  )}

                  {/* 계약일 */}
                  <div className="pdf-section text-center mt-6">
                    <p className="text-sm font-medium text-gray-900">
                      {tab === 'extension2'
                        ? room.secondExtensionSignatureDate?.toLocaleDateString('ko-KR')
                        : tab === 'extension1'
                          ? room.extensionSignatureDate?.toLocaleDateString('ko-KR')
                          : room.signatureDate?.toLocaleDateString('ko-KR')}
                    </p>
                  </div>
                </>
              );
            })()}
          </div>

            {/* 서명 및 날인 정보 표시 */}
          <div className="pdf-section mt-12 border-t pt-8">
            <div className="flex flex-col gap-8 md:grid md:grid-cols-2 md:grid-rows-2 md:gap-x-8 md:gap-y-4">
              {/* 임차인 정보 (row 1, col 1) */}
              <div className="md:col-start-1 md:row-start-1">
                <h3 className="font-semibold text-gray-900 mb-4">임차인 서명</h3>
                <div className="space-y-2">
                  <div className="flex">
                    <span className="font-medium text-gray-700 w-32">임차인:</span>
                    <span className="text-gray-900">
                      {tab === 'extension2'
                        ? room.secondExtensionSignatureName
                        : tab === 'extension1'
                          ? room.extensionSignatureName
                          : room.signatureName}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="font-medium text-gray-700 w-32">주소:</span>
                    <span className="text-gray-900 min-w-0 flex-1 break-words">
                      {(() => {
                        const contractContent =
                          tab === 'extension2'
                            ? room.secondExtensionContractContent
                            : tab === 'extension1'
                              ? room.extensionContractContent
                              : room.contractContent;
                        return contractContent?.signerInfo?.address || (room as any).signatureAddress || '-';
                      })()}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="font-medium text-gray-700 w-32">주민등록번호:</span>
                    <span className="text-gray-900">
                      {(() => {
                        const contractContent =
                          tab === 'extension2'
                            ? room.secondExtensionContractContent
                            : tab === 'extension1'
                              ? room.extensionContractContent
                              : room.contractContent;
                        return (
                          contractContent?.signerInfo?.residentNumber ||
                          (room as any).signatureResidentNumber ||
                          '-'
                        );
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* 임대인 정보 (row 1, col 2) */}
              <div className="md:col-start-2 md:row-start-1">
                <h3 className="font-semibold text-gray-900 mb-4">임대인 날인</h3>
                <div className="space-y-2">
                  <div className="flex">
                    <span className="font-medium text-gray-700 w-20">임대인:</span>
                    <span className="text-gray-900">부산항만공사 (602-81-31628)</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium text-gray-700 w-20"></span>
                    <span className="text-gray-900">사장 송상근</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium text-gray-700 w-20"></span>
                    <span className="text-gray-900">부산광역시 중구 대교로 122 </span>
                  </div>
                </div>
              </div>

              {/* 임차인 서명 이미지 (row 2, col 1) */}
              <div className="md:col-start-1 md:row-start-2">
                {(() => {
                  const signatureUrl =
                    tab === 'extension2'
                      ? room.secondExtensionSignatureUrl
                      : tab === 'extension1'
                        ? room.extensionSignatureUrl
                        : room.signatureUrl;
                  return signatureUrl ? (
                    <div className="border-2 border-gray-300 rounded-md p-8 bg-gray-50 h-40 flex items-center justify-center">
                      <img src={signatureUrl} alt="임차인 서명" className="max-w-full max-h-full h-auto" />
                    </div>
                  ) : (
                    <div className="border-2 border-gray-300 rounded-md p-8 bg-gray-50 text-gray-500 text-center h-40 flex items-center justify-center">
                      서명 이미지를 찾을 수 없습니다.
                    </div>
                  );
                })()}
              </div>

              {/* 임대인 날인 박스 (row 2, col 2) */}
              <div className="md:col-start-2 md:row-start-2">
                <div className="border-2 border-gray-400 border-dashed rounded-md p-8 bg-gray-50 text-center h-40 flex flex-col items-center justify-center">
                  <div className="text-gray-600">
                    <svg className="h-8 w-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm font-medium mb-2">임대인 날인</p>
                    <div className="space-y-1 text-xs text-gray-500">
                      <p>부산항만공사</p>
                      <p>(직인)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

	        {/* 임대인 날인본 관리 섹션 */}
	        <div className="px-8 pb-8 border-t border-gray-200">
	          <h3 className="text-lg font-semibold text-gray-900 mb-6 pt-8">
	            항만공사 {tab === 'extension2' ? '2차 연장' : tab === 'extension1' ? '연장' : ''}계약 날인본 관리
	          </h3>
	          
	          {(() => {
	            const landlordContractUrl =
	              tab === 'extension2'
	                ? room.secondExtensionLandlordSignedContractUrl
	                : tab === 'extension1'
	                  ? room.extensionLandlordSignedContractUrl
	                  : room.landlordSignedContractUrl;
	            const landlordContractUploadedAt =
	              tab === 'extension2'
	                ? room.secondExtensionLandlordSignedContractUploadedAt
	                : tab === 'extension1'
	                  ? room.extensionLandlordSignedContractUploadedAt
	                  : room.landlordSignedContractUploadedAt;
	              
	            return landlordContractUrl;
	          })() ? (
            <div className="space-y-4">
                            <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
	                    <div>
	                      <p className="text-green-800 font-medium">
	                        항만공사 {tab === 'extension2' ? '2차 연장' : tab === 'extension1' ? '연장' : ''}계약 날인본이 등록되어 있습니다.
	                      </p>
	                      <p className="text-green-600 text-sm">
	                        업로드일: {(() => {
	                          const uploadedAt =
	                            tab === 'extension2'
	                              ? room.secondExtensionLandlordSignedContractUploadedAt
	                              : tab === 'extension1'
	                                ? room.extensionLandlordSignedContractUploadedAt
	                                : room.landlordSignedContractUploadedAt;
	                          return uploadedAt?.toLocaleString('ko-KR');
	                        })()}
	                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
	                    <a
	                      href={(() => {
	                        return tab === 'extension2'
	                          ? room.secondExtensionLandlordSignedContractUrl
	                          : tab === 'extension1'
	                            ? room.extensionLandlordSignedContractUrl
	                            : room.landlordSignedContractUrl;
	                      })()}
	                      target="_blank"
	                      rel="noopener noreferrer"
	                      className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
	                    >
                      다운로드
                    </a>
                  </div>
                </div>
              </div>
              
              {/* 새 파일로 교체 */}
	              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
	                <p className="text-gray-700 mb-3">
	                  새로운 항만공사 {tab === 'extension2' ? '2차 연장' : tab === 'extension1' ? '연장' : ''}계약 날인본으로 교체:
	                </p>
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    id="landlord-contract-upload"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100"
                  />
                  {uploadedFile && (
                    <button
                      onClick={uploadLandlordSignedContract}
                      disabled={uploading}
                      className="px-4 py-2 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {uploading ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          업로드 중...
                        </>
                      ) : (
                        '교체하기'
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="h-5 w-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
	                  <p className="text-orange-800 font-medium">
	                    항만공사 {tab === 'extension2' ? '2차 연장' : tab === 'extension1' ? '연장' : ''}계약 날인본이 등록되지 않았습니다.
	                  </p>
                </div>
	                <p className="text-orange-700 text-sm">
	                  {tab === 'extension2' ? '2차 연장' : tab === 'extension1' ? '연장' : ''}계약서를 다운로드하여 항만공사 날인을 받은 후, 스캔한 PDF 파일을 업로드해주세요.
	                </p>
              </div>
              
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  id="landlord-contract-upload"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                />
                {uploadedFile && (
                  <button
                    onClick={uploadLandlordSignedContract}
                    disabled={uploading}
                    className="px-4 py-2 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        업로드 중...
                      </>
                    ) : (
                      '업로드'
                    )}
                  </button>
                )}
              </div>
              
              {uploadedFile && (
                <p className="mt-2 text-sm text-gray-600">
                  선택된 파일: {uploadedFile.name} ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
          )}
          </div>

          {/* 돌아가기 버튼 */}
	        <div className="px-8 pb-8 flex justify-end">
	            <button
	              onClick={goToRoomsList}
	              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
	            >
	              호실 관리로 돌아가기
	            </button>
	        </div>
	      </div>
	    </div>
	  </div>
	  );
	};

export default ViewContractPage; 
